/**
 * GlobalAuthBanner — sticky, non-intrusive banner shown to guests only.
 * Reminds users they're browsing as a guest and pushes the 14-day trial.
 * Dismissible per-session; auto-reappears on next page load.
 */
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { X, Sparkles } from "lucide-react";
import { useGuest } from "../contexts/GuestContext";

const DISMISS_KEY = "guest_banner_dismissed_session";

export default function GlobalAuthBanner() {
  const { isGuest } = useGuest();
  const navigate = useNavigate();
  const [dismissed, setDismissed] = useState(() => {
    try {
      return sessionStorage.getItem(DISMISS_KEY) === "1";
    } catch {
      return false;
    }
  });

  if (!isGuest || dismissed) return null;

  return (
    <div
      className="sticky top-0 z-40 w-full bg-gradient-to-r from-emerald-600 via-green-600 to-emerald-700 text-white shadow-md"
      data-testid="global-auth-banner"
    >
      <div className="max-w-6xl mx-auto flex items-center gap-3 px-3 py-2 sm:px-4">
        <Sparkles className="h-4 w-4 flex-shrink-0 text-emerald-100" />
        <p className="flex-1 text-xs sm:text-sm leading-tight">
          <span className="font-semibold">Browsing as a guest.</span>{" "}
          <span className="text-emerald-100 hidden sm:inline">
            Unlock meal plans, deal alerts, and receipt scanning —{" "}
          </span>
          <button
            type="button"
            onClick={() => navigate("/auth?mode=signup")}
            className="underline font-semibold hover:text-white"
            data-testid="global-auth-banner-cta"
          >
            start your 14-day free trial
          </button>
          .
        </p>
        <button
          type="button"
          onClick={() => {
            try {
              sessionStorage.setItem(DISMISS_KEY, "1");
            } catch { /* ignore quota errors */ }
            setDismissed(true);
          }}
          className="flex-shrink-0 p-1 hover:bg-white/15 rounded-full"
          aria-label="Dismiss banner"
          data-testid="global-auth-banner-dismiss"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      </div>
    </div>
  );
}
